import { createInterval, Droppable, EventListener } from '../../../utils'

import type { ConnectwareError } from '../../../domain'

type Retriever<R> = () => Promise<R>
type Comparator<R> = (a: R | ConnectwareError, b: R | ConnectwareError) => boolean

export class Fetcher<R> {
    private readonly listeners = new EventListener<R | ConnectwareError>()

    private readonly droppable = new Droppable()

    private current: R | ConnectwareError | null = null

    /**
     * @description the utility class that assists with subscribing to Connectware HTTP endpoints
     *
     * @param retrieve the function that will retrieve the async resources
     * @param comparator the function that will compare the current and previous results
     */
    constructor (private readonly retrieve: Retriever<R>, private readonly comparator: Comparator<R>) {}

    private trigger (data: R | ConnectwareError): void {
        this.listeners.trigger(data)
    }

    private async run (trigger: boolean): Promise<void> {
        try {
            const data = await this.retrieve()

            /**
             * Swap previous with new value
             */
            const previous = this.current || data
            this.current = data

            if (trigger || !this.comparator(previous, data)) {
                this.trigger(this.current)
            }
        } catch (e: unknown) {
            this.current = e as ConnectwareError
            this.trigger(this.current)
        }
    }

    /**
     * Makes another fetch request
     */
    update (trigger = false): void {
        void this.run(trigger)
    }

    /**
     * Starts the timed pulling of resources
     *
     * Do **not** call this twice without stopping it first
     *
     * @param interval in milliseconds, needs to be less than 32-bit integer
     * @see https://developer.mozilla.org/en-US/docs/Web/API/setInterval#sect1
     */
    start (interval: number): void {
        void this.update(true)

        this.droppable.onDrop(createInterval(() => this.update(), interval))
    }

    /**
     * Stops the previously started fetcher
     */
    stop (): void {
        this.droppable.drop()
    }

    /**
     * @param handler will be called whenever changes are detected
     */
    onChange (handler: (e: R | ConnectwareError) => void): VoidFunction {
        return this.listeners.on(handler)
    }
}
